import {
  Loadable,
  Model,
  ModelAttributes,
  ModelDescription,
  model,
  modelConfig,
  reference,
} from "@mixitone/oom";
import { default as Tournament, default as TournamentCompetition } from "./TournamentCompetition";
import TournamentEntry from "./TournamentEntry";
import TournamentPlayer from "./TournamentPlayer";
import { setAccountId } from "./concerns/SetAccountId";

const config = modelConfig(
  {
    account_id: {
      type: "string",
    },
    admin_signup_notification_sent_at: {
      type: "datetime",
    },
    club_id: {
      type: "string",
    },
    created_at: {
      type: "datetime",
    },
    is_deleted: {
      type: "boolean",
    },
    partner_code: {
      type: "string",
    },
    partner_email: {
      type: "string",
    },
    partner_email_sent_at: {
      type: "datetime",
    },
    partner_joined: {
      type: "boolean",
    },
    registration_received_email_sent_at: {
      type: "datetime",
    },
    tournament_competition_id: {
      type: "string",
    },
    tournament_entry_id: {
      type: "string",
    },
    tournament_player_id: {
      type: "string",
    },
    updated_at: {
      type: "datetime",
    },
    withdrawn_at: {
      type: "datetime",
    },
  },
  {},
  {
    player: reference(() => TournamentPlayer as any, "tournament_player_id"),
    entry: reference(() => TournamentEntry as any, "tournament_entry_id"),
    competition: reference(() => Tournament as any, "tournament_competition_id"),
  },
);

interface TournamentRegistration extends ModelAttributes<typeof config> {
  player: Loadable<TournamentPlayer>;
  entry: Loadable<TournamentEntry>;
  competition: Loadable<TournamentCompetition>;
}

@setAccountId
@model("tournament_registrations", config)
class TournamentRegistration extends Model<typeof config> {
  static readonly modelDescription: ModelDescription = {
    description:
      "A tournament registration links a player to a competition and entry within a tournament. " +
      "It tracks the player's participation status, partner requirements for doubles events, " +
      "and withdrawal status. The withdrawn_at field is only used for withdrawals during active play." +
      "Once created, the tournament_player_id cannot be changed. " +
      "You can update tournament_entry_id to move the player to a different entry, or create a new registration to add them to a different competition.",
    availableFields: Object.keys(config.fields),
    relationships: {
      player: "tournament_players where id equals this tournament_player_id",
      entry: "tournament_entries where id equals this tournament_entry_id",
      competition: "tournament_competitions where id equals this tournament_competition_id",
    },
  };

  get name() {
    if (this.player.loaded) return this.player.name;
  }

  get isWithdrawn() {
    return !!this.withdrawn_at;
  }

  /**
   * Get the status of this registration
   * @returns A status string: "Completed", "Withdrawn", "Payment required", "Waiting confirmation", or "Confirmed"
   */
  get status():
    | "completed"
    | "withdrawn"
    | "payment_required"
    | "waiting_partner"
    | "waiting_confirmation"
    | "confirmed" {
    if (!this.player.loaded || !this.competition.loaded) {
      throw new Error("Player or competition not loaded");
    }

    if (this.competition.isCompleted) {
      return "completed";
    }

    if (this.player.isWithdrawn || this.isWithdrawn) {
      return "withdrawn";
    }

    if (this.competition.session.requiresPayment && !this.player.isPaid) {
      return "payment_required";
    } else if (this.competition.requiredPlayersPerEntry === 2) {
      // Check if partner is assigned by looking at entry registrations
      // This handles the case where admin manually assigns a partner
      let hasPartner = this.partner_joined;

      if (!hasPartner && this.entry.loaded && this.entry.id) {
        // Check if entry has the required number of registrations
        if (this.entry.registrations.loaded) {
          const validRegistrations = this.entry.registrations.filter(
            (reg) => !reg.isWithdrawn && !reg.is_deleted,
          );
          hasPartner = validRegistrations.length >= this.competition.requiredPlayersPerEntry;
        }
      }

      if (!hasPartner) {
        return "waiting_partner";
      }
    }

    if (!this.player.isConfirmed) {
      return "waiting_confirmation";
    }

    return "confirmed";
  }
}

export default TournamentRegistration;
